import { useEffect, useState } from 'react'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { OrderStatusType } from '@/entities/gridTables/model/OrderStatusType'
import { OLMSApiService } from '@/shared/apis'
import { Icon } from '@/shared/components'
import { pxToRem } from '@/shared/styles/themes'
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { Box, Chip, css, Grid2 as Grid, styled, Typography } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

const OrderHistoryRecordEnum = {
  StatusUpdates: 1,
  Notification: 2,
  Exception: 3,
  Imported: 4,
  Created: 5,
  Duplicated: 6,
  Published: 7,
  InboxPublished: 8,
}

export type OrderHistoryRecordType = typeof OrderHistoryRecordEnum[keyof typeof OrderHistoryRecordEnum]

type OrderHistoryRecord = {
  type: OrderHistoryRecordType
  userId: EntityId
  userName: string
  date: string
  time: string

  runsheetId?: number
  runsheetName?: string
  runId?: number
  runName?: string
  statusType?: OrderStatusType
  message?: string

  notificationType?: number
  notificationEventType?: number
  recipientType?: number
  sendTo?: string

  exceptionType?: number
}

export type OrderHistoryTabPanelProps = {
  orderId: EntityId | null
}

export const OrderHistoryTabPanel: React.FC<OrderHistoryTabPanelProps> = ({ orderId }) => {
  const [orderHistoryRecords, setOrderHistoryRecords] = useState<OrderHistoryRecord[] | null>(null)
  const { data: exceptionTypes } = useDropdown('exceptionTypes')
  const { data: orderStatuses } = useDropdown('orderStatuses')
  const { data: notificationTypes } = useDropdown('notificationTypes')
  const { data: recipientTypes } = useDropdown('recipientTypes')
  const { data: notificationEventTypes } = useDropdown('notificationOrderEventTypes')

  useEffect(() => {
    if (!orderId) {
      setOrderHistoryRecords(null)

      return
    }

    OLMSApiService.get<OrderHistoryRecord[]>(`/order/history/${orderId}`)
      .then(records => setOrderHistoryRecords(records))
  }, [orderId])

  return (
    <>
      {orderHistoryRecords && orderHistoryRecords.map((record, index) => (
        <HistoryRecord
          key={`${record.type}-${index}`}
          record={record}
          orderStatuses={orderStatuses}
          notificationTypes={notificationTypes}
          recipientTypes={recipientTypes}
          notificationEventTypes={notificationEventTypes}
          exceptionTypes={exceptionTypes}
        />
      ))}
    </>
  )
}


type HistoryRecordProps = {
  record: OrderHistoryRecord
  orderStatuses: DropdownOption[]
  notificationTypes: DropdownOption[]
  recipientTypes: DropdownOption[]
  notificationEventTypes: DropdownOption[]
  exceptionTypes: DropdownOption[]
}

const HistoryRecord: React.FC<HistoryRecordProps> = ({
  record,
  orderStatuses,
  notificationTypes,
  recipientTypes,
  notificationEventTypes,
  exceptionTypes,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const getDescription = () => {
    if (record.type === OrderHistoryRecordEnum.StatusUpdates) {
      const statusRec = orderStatuses.find(s => s.value === record.statusType)

      if (!statusRec) return ''

      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Chip
            label={statusRec.label}
            style={{
              height: pxToRem(24),
              color: statusRec.fontColor,
              backgroundColor: statusRec.backgroundColor,
              marginRight: pxToRem(4),
            }}
          />
          <Typography>
            by {record.userName}
            {record.runName ? ` for ${record.runName}` : ''}
            {` on ${record.runsheetName}`}
          </Typography>
        </Box>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Notification) {
      const nType = notificationTypes?.find(nt => nt.value === record.notificationType)?.label || ''
      const neType = notificationEventTypes?.find(net => net.value === record.notificationEventType)?.label || ''
      const rType = recipientTypes?.find(nt => nt.value === record.recipientType)?.label || ''

      const description = `${neType} ${nType} sent to ${rType} ${record.sendTo}`

      return (
        <StyledNotification>
          <Typography variant="body2">
            {description}
          </Typography>
          <Icon sx={{ fontSize: pxToRem(14), marginRight: pxToRem(8) }} icon={isExpanded ? faChevronDown : faChevronRight} />
        </StyledNotification>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Exception) {
      const eType = exceptionTypes?.find(nt => nt.value === record.exceptionType)?.label || ''
      const description = `Exception Type: ${eType} Comment: ${record.message} by ${record.userName}`

      return (
        <Box>
          <Typography>
            {description}
          </Typography>
        </Box>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Imported) {
      return (
        <Typography>
          {`Imported by ${record.userName} via file ${record.message}`}
        </Typography>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Created) {
      return (
        <Typography>
          {`Created/updated manually by ${record.userName}`}
        </Typography>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Duplicated) {
      return (
        <Typography>
          {`${record.message} by ${record.userName}`}
        </Typography>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.Published) {
      return (
        <Typography>
          {`Published to Runsheet by ${record.userName}`}
        </Typography>
      )
    }
    else if (record.type === OrderHistoryRecordEnum.InboxPublished) {
      return (
        <Typography>
          {`Published from Inbox to Runsheet by ${record.userName}`}
        </Typography>
      )
    }

    return null
  }

  return (
    <>
      <StyledRowGrid
        container
        direction={'row'}
        alignItems={'center'}
        spacing={pxToRem(12)}
        $clickable={record.type === OrderHistoryRecordEnum.Notification}
        onClick={() => {
          if (record.type === OrderHistoryRecordEnum.Notification) {
            setIsExpanded((prev => !prev))
          }
        }}
      >
        <Grid>
          <StyledTimestampTypography variant="body1">
            {record.date}
          </StyledTimestampTypography>
        </Grid>
        <Grid>
          <StyledTimestampTypography variant="body1">
            {record.time}
          </StyledTimestampTypography>
        </Grid>
        <Grid flexGrow={1}>
          {getDescription()}
        </Grid>

      </StyledRowGrid>
      {isExpanded && record.message && (
        <StyledMessage>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            "{record.message}"
          </Typography>
        </StyledMessage>
      )}
    </>
  )
}

const StyledRowGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== '$clickable',
}) <{ $clickable: boolean }>`
  padding: ${pxToRem(8)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.divider};

  ${props => props.$clickable && css`
    &:hover {
      background-color: ${props.theme.palette.action.hover};
    }
  `}
`

const StyledTimestampTypography = styled(Typography)`
  font-weight: 500;
`

const StyledNotification = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  cursor: pointer;
`

const StyledMessage = styled(Box)`
  margin-top: ${pxToRem(4)};
  padding: ${pxToRem(8)} ${pxToRem(12)};
  background-color: ${props => props.theme.palette.action.hover};
  border-left: 2px solid ${props => props.theme.palette.primary.main};
  border-radius: ${props => props.theme.shape.borderRadius}px;
`
